import { useEffect, useCallback } from "react"
import { Bot, User, Users, LifeBuoy, Megaphone, Globe, Loader2, CloudOff } from 'lucide-react'

import { ConversationList, Conversation } from "@chatscope/chat-ui-kit-react"

import { Channel, ChannelType } from "../../../../shared-src/channel"

import { useChatStore } from "../../state/chat-store"
import { useSessionContext } from '@/state/session-context'
import { useAppVisibility } from '@/state/use-app-visibility'
import { fetchChannels } from '@/state/chat-actions'
import * as env from '@/env'
import { useUserStore } from "@/state/user-store"
import ChannelsHeader from "./channels-header"
import { getColorSet } from '@/theme/color'
import { useOnboardingBannerState } from '@/state/use-onboarding-banner-state'


interface ChannelsSidebarProps {
	isOpen: boolean
	onClose: () => void
}

export function ChannelsSidebar(props: ChannelsSidebarProps) {
	const { session } = useSessionContext()

	const channels = useChatStore(state => state.channels)
	const channelsStatus = useChatStore(state => state.channelsStatus)
	const activeChannelId = useChatStore(state => state.activeChannelId)
	const setActiveChannelId = useChatStore(state => state.setActiveChannelId)
	const unreadChannelIds = useChatStore(state => state.unreadChannelIds)

	const user = useUserStore(state => state.user)

	const handleFetchChannels = useCallback(async () => {
		if (!session?.userId)
			return

		await fetchChannels({ userId: session.userId })
	}, [session?.userId])

	useEffect(() => {
		handleFetchChannels()
	}, [handleFetchChannels])

	useAppVisibility({
		onForeground: handleFetchChannels,
		enabled: !!session?.userId
	})

	const colorSet = getColorSet({ senderColor: user?.color })

	const { isVisible: onboardingBannerVisible } = useOnboardingBannerState()
	const devBannerVisible = !env.isProduction

	let topPadding = ''
	if (devBannerVisible && onboardingBannerVisible)
		topPadding = 'pt-20'
	else if (devBannerVisible)
		topPadding = 'pt-12'
	else if (onboardingBannerVisible)
		topPadding = 'pt-8'

	return (
		<div
			className={`
				fixed md:static inset-y-0 left-0 z-30 bg-white border-r transform transition-transform duration-200 ease-in-out
				w-80 md:w-64
				${props.isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
				${topPadding} md:pt-0
			`}
			style={{
				borderColor: colorSet.border
			}}
		>
			<div className="h-full flex flex-col">
				<ChannelsHeader onClose={props.onClose} />
				<div className="flex-1 overflow-y-auto">
					{channelsStatus !== 'loaded' ? (
						//	Rendered INSTEAD of ConversationList, not inside it. ConversationList
						//	declares `children: allowedChildren([Conversation])` and wraps every child
						//	in `li` inside a `ul`, so a plain icon child would draw a dev propTypes
						//	error and inherit conversation-row markup and styling. It does have a
						//	`loading` prop that overlays its own spinner without tripping that check,
						//	but it draws the library's Loader, and every other spinner in this app is
						//	a `Loader2` -- and it has nothing to offer the error case, which would
						//	then be drawn by a second, unrelated mechanism.
						//
						//	`not-loaded` and `error` are drawn differently ON PURPOSE. A spinner
						//	asserts an operation is in flight; once the fetch has failed none is, and
						//	per ha-420 a task that is re-delivered and later succeeds cannot notify
						//	the frontend. So a spinner here would be a permanent active misstatement,
						//	while the error icon is merely stale. Neither state self-heals: a reload,
						//	or backgrounding and foregrounding the tab, is what repairs it.
						//
						//	`role="img"` is load-bearing, not decoration: lucide spreads arbitrary
						//	props onto a bare `svg` and sets no role, and a bare `svg` is not reliably
						//	exposed to the accessibility tree, so `aria-label` alone can go unread.
						<div className="flex items-center justify-center h-full text-gray-400">
							{channelsStatus === 'error'
								? <CloudOff className="w-8 h-8" role="img" aria-label="Could not load conversations" />
								: <Loader2 className="w-8 h-8 animate-spin" role="img" aria-label="Loading conversations" />}
						</div>
					) : (
						<ConversationList>
							{channels.map((ch) => {
								const isUnread = unreadChannelIds.has(ch.id) && ch.id !== activeChannelId
								return (
									<Conversation
										key={ch.id}
										name={ch.displayId}
										onClick={() => {
											setActiveChannelId(ch.id)
											props.onClose()
										}}
										active={ch.id === activeChannelId}
										className={isUnread ? "ha-channel-unread" : undefined}
										style={{
											'--conversation-active-bg-color': colorSet.background,
											'--conversation-name-text-color': (ch.id === activeChannelId) ? colorSet.dark : colorSet.text,
											'--conversation-name-font-weight': (ch.id === activeChannelId) ? 'bold' : 'normal',
											'--ha-unread-color': colorSet.border,
										} as React.CSSProperties}
									/>
								)
							})}
						</ConversationList>
					)}
				</div>
			</div>
		</div>
	)
} 

export function ChannelIcon(props: { channel: Channel, className?: string }) {
	const icon = () => { switch (props.channel.type) {
		case ChannelType.bot: return <Bot className="w-5 h-5" />
		case ChannelType.dm: return <User className="w-5 h-5" />
		case ChannelType.hangaroundGroup: return <Users className="w-5 h-5" />
		case ChannelType.public: return <Globe className="w-5 h-5" />
		case ChannelType.announcement: return <Megaphone className="w-5 h-5" />
		case ChannelType.support: return <LifeBuoy className="w-5 h-5" />
		default:
			return <Globe className="w-5 h-5" />
	}}

	return <div className={props.className}>
		{icon()}
	</div>
}
